import React, { useCallback, useEffect, useRef, useState } from "react";

import useEsTelefono from "../../../../utils/esTelefono";
import { entregarArchivo } from "../../../../utils/entregarArchivo";
import { AlumnoDelLibro, ApiNotasPersonal, CeldaLibro, ColumnaLibro, LibroDeNotas } from "../../../../services/v2/classPortal.service";
import { Aviso, Avatar, exito, LineaAviso, mensajeDeError, problema, useAlVolver } from "../comun";
import { esNotaBaja, puntosTxt } from "../tareas/comunTareas";
import { BarraCategoria, esBaja, notaTxt, pesoTxt, textoAsistencia } from "./comunNotas";
import "../tareas/tareas.css";
import "./notas.css";

/*
	Pestaña Notas del portal (personal, Parte C): el Libro De Notas. Alumnos × tareas agrupadas por categoría, el % de cada
	categoría, la asistencia (automática) y el promedio, todo calculado por el servidor. Las celdas sin rúbrica se editan aquí
	(mismos topes y bitácora que Calificar); con rúbrica, o con una entrega esperando nota, se abre Calificar. Publicar Notas
	devuelve de una vez lo calificado que el alumno aún no ve. En el celular, una tarjeta por alumno. Carga al abrir, al volver
	a la pestaña y con Actualizar; sin sondeos.
*/

interface Props { api: ApiNotasPersonal; cursoTitulo: string; onCalificar: (postId: number, quiz: boolean) => void }

/**
 * Lo que se puede escribir directo en la celda (el servidor lo vuelve a validar). Una práctica En Clase no tiene entrega, así que
 * "espera nota" también se escribe aquí. Un quiz nunca: su nota sale de los intentos (se abre Resultados).
 */
const editable = (col: ColumnaLibro, c: CeldaLibro): boolean => {
	if (col.rubric || col.delivery === "quiz" || c.state === "na" || c.state === "repetir") return false
	if (c.state === "calificar" || c.state === "pendiente") return col.delivery === "en_clase"
	return true
}

const DESCRIPCION: Record<string, string> = {
	ok: "Devuelta", sinDevolver: "Calificada, el alumno aún no la ve", calificar: "Entregó, espera nota", pendiente: "Aún no vence",
	falta: "No entregó y ya venció", repetir: "Le pidieron que repita", cerrada: "Tarea cerrada sin entrega: cuenta 0", na: "Esta tarea no es para este alumno",
}

const Celda: React.FC<{ col: ColumnaLibro; c: CeldaLibro; alumno: string; minimo: number; onGuardar: (puntos: number) => Promise<boolean>; onAbrir: () => void }> = ({ col, c, alumno, minimo, onGuardar, onAbrir }) => {
	const [editando, setEditando] = useState<boolean>(false)
	const [valor, setValor] = useState<string>("")
	const [guardando, setGuardando] = useState<boolean>(false)
	const cancelado = useRef<boolean>(false)
	const tarde = c.lateDiscount || 0
	const descripcion = c.state === "calificar" && col.delivery === "en_clase" ? "Se califica en clase, sin nota aún" : DESCRIPCION[c.state]
	const etiqueta = col.title + ", " + alumno + ": " + descripcion + (c.score !== null ? ", " + puntosTxt(c.score) + " de " + puntosTxt(col.points) : "")
	// Se escribe la nota ANTES del descuento por tarde (como en Calificar); el servidor vuelve a restarlo.
	const bruta = c.score !== null ? puntosTxt(c.score + tarde) : ""

	const empezar = () => {
		if (!editable(col, c)) { onAbrir(); return }
		cancelado.current = false
		setValor(bruta)
		setEditando(true)
	}
	const terminar = async () => {
		if (cancelado.current || guardando) return
		const t = valor.trim().replace(",", ".")
		if (t === "" || t === bruta) { setEditando(false); return }
		const n = Number(t)
		if (isNaN(n) || n < 0 || n > col.points) { cancelado.current = true; setEditando(false); await onGuardar(NaN); return }
		setGuardando(true)
		const ok = await onGuardar(n)
		setGuardando(false)
		if (ok) setEditando(false)
	}

	if (editando) {
		return <input className="ntEntrada" autoFocus inputMode="decimal" aria-label={"Nota de " + alumno + " en " + col.title + " (de " + puntosTxt(col.points) + (tarde > 0 ? ", antes del descuento por tarde" : "") + ")"} value={valor} disabled={guardando}
			onFocus={(e) => e.target.select()} onChange={(e) => setValor(e.target.value)} onBlur={terminar}
			onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur() } if (e.key === "Escape") { cancelado.current = true; setEditando(false) } }} />
	}
	if (c.state === "na") return <span className="ntVacia" title={DESCRIPCION.na} aria-label={etiqueta}>—</span>
	if (c.state === "calificar" && !editable(col, c)) return <button type="button" className="ntAccion" title={col.delivery === "quiz" ? "Tiene preguntas de Párrafo por calificar" : DESCRIPCION.calificar} aria-label={etiqueta} onClick={onAbrir}>Calificar</button>
	if (c.state === "calificar") return <button type="button" className="ntCelda ntVacia" title={descripcion + ": escribe su nota"} aria-label={etiqueta} onClick={empezar}>—</button>
	if (c.state === "repetir") return <button type="button" className="ntAccion ntAccionOro" title={DESCRIPCION.repetir} aria-label={etiqueta} onClick={onAbrir}>Repetir</button>
	if (c.state === "pendiente" && !editable(col, c)) return <span className="ntVacia" title={DESCRIPCION.pendiente} aria-label={etiqueta}>—</span>
	if (c.state === "falta") return <button type="button" className="ntCelda ntFalta" title={DESCRIPCION.falta + (col.rubric ? "" : ". Escribe su nota (normalmente 0).")} aria-label={etiqueta} onClick={empezar}>Falta</button>
	if (c.state === "pendiente") return <button type="button" className="ntCelda ntVacia" title={DESCRIPCION.pendiente} aria-label={etiqueta} onClick={empezar}>—</button>
	const baja = c.score !== null && esNotaBaja(c.score, col.points, minimo)
	return (
		<button type="button" className={"ntCelda" + (c.state === "sinDevolver" ? " ntSinPublicar" : "") + (baja || c.state === "cerrada" ? " ntCeldaOro" : "")}
			title={DESCRIPCION[c.state] + (tarde > 0 ? ". Entrega tarde: −" + puntosTxt(tarde) + " pts" : "") + (col.rubric ? ". Con rúbrica: se cambia en Calificar." : col.delivery === "quiz" ? ". Quiz: se cambia en Resultados." : "")} aria-label={etiqueta} onClick={empezar}>
			{c.score !== null ? puntosTxt(c.score) : "0"}
		</button>
	)
}

const Promedio: React.FC<{ n: number | null; minimo: number }> = ({ n, minimo }) =>
	n === null ? <span className="ntVacia" title="Aún no tiene nada que cuente">–</span>
		: esBaja(n, minimo) ? <span className="ntPromedioOro">{notaTxt(n)}</span> : <span className="ntPromedio">{notaTxt(n)}</span>

interface Grupo { nombre: string; peso: number | null; asistencia: boolean; sinCategoria: boolean; cols: number[] }

const armarGrupos = (l: LibroDeNotas): Grupo[] => {
	const usadas = new Set<number>()
	const grupos: Grupo[] = l.scheme.categories.map((c) => {
		const cols = c.esAsistencia ? [] : l.tasks.map((t, i) => (t.categoryOk && (t.category || "").trim().toLowerCase() === c.name.toLowerCase() ? i : -1)).filter((i) => i >= 0)
		cols.forEach((i) => usadas.add(i))
		return { nombre: c.name, peso: c.weight, asistencia: c.esAsistencia, sinCategoria: false, cols }
	})
	const sueltas = l.tasks.map((_, i) => i).filter((i) => !usadas.has(i))
	if (sueltas.length > 0) grupos.push({ nombre: "Sin Categoría", peso: null, asistencia: false, sinCategoria: true, cols: sueltas })
	return grupos
}

const LibroDeNotasPanel: React.FC<Props> = ({ api, cursoTitulo, onCalificar }) => {
	const telefono = useEsTelefono()
	const [libro, setLibro] = useState<LibroDeNotas | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [aviso, setAviso] = useState<Aviso | null>(null)
	const [ocupado, setOcupado] = useState<boolean>(false)
	const [abierto, setAbierto] = useState<number | null>(null)

	const cargar = useCallback(() => {
		api.get().then((l) => { setLibro(l); setError(null) }).catch((e) => setError(mensajeDeError(e, "No pudimos cargar las notas.")))
	}, [api])
	useEffect(() => { cargar() }, [cargar])
	useAlVolver(cargar)

	if (error && !libro) return <div className="pcAviso pcOro" role="alert">{error}</div>
	if (libro === null) return <div className="pcCargando">Cargando…</div>

	const l = libro
	const minimo = l.minGrade
	const grupos = armarGrupos(l)
	const s = l.summary

	const guardar = async (col: ColumnaLibro, alumno: AlumnoDelLibro, puntos: number): Promise<boolean> => {
		setAviso(null)
		if (isNaN(puntos)) { setAviso(problema("La nota de " + col.title + " va de 0 a " + puntosTxt(col.points) + " pts.")); return false }
		try { setLibro(await api.cell(col.id, alumno.id, puntos)); return true } catch (e) { setAviso(problema(mensajeDeError(e))); return false }
	}

	const descargar = async () => {
		setOcupado(true); setAviso(null)
		try {
			const r = await api.excel()
			await entregarArchivo(r.blob, r.name || ("Notas " + cursoTitulo).replace(/[\\/:*?"<>|]+/g, " ").trim() + ".xlsx", "Portal de Clase")
		} catch (e: any) {
			let m = "No pudimos generar el Excel. Intenta otra vez."
			const d = e?.response?.data
			if (d instanceof Blob) { try { m = JSON.parse(await d.text()).message || m } catch { /* se queda el genérico */ } }
			setAviso(problema(m))
		} finally { setOcupado(false) }
	}

	const publicar = async () => {
		if (s.unreturned === 0) { setAviso(exito("No hay notas por publicar: todo lo calificado ya lo ven los alumnos.")); return }
		if (!window.confirm("¿Publicar " + (s.unreturned === 1 ? "1 nota" : s.unreturned + " notas") + " a los alumnos? Verán su nota y tu comentario, y contará en su promedio.")) return
		setOcupado(true); setAviso(null)
		try {
			const r = await api.publish()
			setLibro(r)
			setAviso(exito(r.returned === 0 ? "No había notas por publicar." : r.returned === 1 ? "Publicamos 1 nota." : "Publicamos " + r.returned + " notas."))
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}

	const categoriaDe = (a: AlumnoDelLibro, nombre: string) => a.categories.find((c) => c.name === nombre)

	const barra = (
		<>
			<div className="ntBarra">
				<div className="ntPesos" aria-label="Cómo se calcula la nota">
					<span className="pcChip pcChipBorde">{l.scheme.sourceLabel}</span>
					{l.scheme.categories.map((c) => <span key={c.name} className="pcChip">{c.name} <b>{pesoTxt(c.weight)}</b></span>)}
				</div>
				<span className="ntEspacio" />
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={cargar}>Actualizar</button>
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={descargar}>Descargar Excel</button>
				<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={publicar}>Publicar Notas{s.unreturned > 0 ? " · " + s.unreturned : ""}</button>
			</div>
			<LineaAviso a={aviso} />
			{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}
			{l.warnings.map((w, i) => <div key={i} className="tkAvisoOro">{w.message}</div>)}
			{s.unreturned > 0 ? <div className="tkAvisoOro">{s.unreturned === 1 ? "1 nota calificada aún no la ve el alumno" : s.unreturned + " notas calificadas aún no las ven los alumnos"} (con borde punteado). Publicar Notas se las manda y cuentan en el promedio.</div> : null}
			{s.toGrade > 0 ? <div className="pcNota">{s.toGrade === 1 ? "1 entrega espera" : s.toGrade + " entregas esperan"} nota: toca "Calificar" para verlas.</div> : null}
		</>
	)

	const resumen = (
		<div className="ntResumen">
			<div className="ntDato"><div>Promedio De La Clase</div><div className={"ntCifra" + (esBaja(s.average, minimo) ? " ntOro" : "")}>{notaTxt(s.average)}</div></div>
			{s.categories.map((c) => (
				<div className="ntDato" key={c.name}><div title={c.name + " · " + pesoTxt(c.weight)}>{c.name} · {pesoTxt(c.weight)}</div><div className={"ntCifra" + (esBaja(c.avg, minimo) ? " ntOro" : "")}>{notaTxt(c.avg)}</div></div>
			))}
			<div className="ntDato"><div>Bajo {notaTxt(minimo)}</div><div className={"ntCifra" + (s.low > 0 ? " ntOro" : "")}>{s.low}</div></div>
		</div>
	)

	if (l.students.length === 0) return <>{barra}<div className="pcVacio">Esta Clase Aún No Tiene Alumnos</div></>

	// ---- celular: una tarjeta por alumno
	if (telefono) {
		return (
			<>
				{barra}
				{resumen}
				<div className="ntLista">
					{l.students.map((a) => {
						const abiertoAqui = abierto === a.id
						return (
							<section key={a.id} className="pcTarjeta ntAlumno" aria-label={a.name}>
								<button type="button" className="ntAlumnoCabeza" aria-expanded={abiertoAqui} onClick={() => setAbierto(abiertoAqui ? null : a.id)}>
									<Avatar a={{ name: a.name, initials: a.initials, photo: a.photo }} size={36} />
									<span>
										<span className="ntNombreCel">{a.name}</span>
										<span className="pcTenue pcChico">{a.attendance.pct !== null ? "Asistencia " + notaTxt(a.attendance.pct) + " %" : "Sin Asistencia Tomada"}</span>
									</span>
									<Promedio n={a.final} minimo={minimo} />
								</button>
								{abiertoAqui ? (
									<div className="ntAlumnoCuerpo">
										<div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "6px 0 8px" }}>
											{a.categories.map((c) => <BarraCategoria key={c.name} c={c} minimo={minimo} asistenciaTxt={c.esAsistencia ? textoAsistencia(a.attendance) : null} />)}
										</div>
										{grupos.filter((g) => g.cols.length > 0).map((g) => (
											<React.Fragment key={g.nombre}>
												<div className="ntCatCel"><span className={g.sinCategoria ? "ntGrupoOro" : undefined}>{g.nombre}{g.sinCategoria ? " · No Cuenta" : ""}</span></div>
												{g.cols.map((i) => {
													const col = l.tasks[i]
													return (
														<div className="ntFilaCel" key={col.id}>
															<span>
																<span className="ntTituloCel">{col.title}</span>
																<small>{[puntosTxt(col.points) + " Pts", col.noWeight ? "Sin Peso" : null, col.closed ? "Cerrada" : col.dueLabel ? "Vence " + col.dueLabel : null].filter(Boolean).join(" · ")}</small>
															</span>
															<Celda col={col} c={a.cells[i]} alumno={a.name} minimo={minimo} onGuardar={(p) => guardar(col, a, p)} onAbrir={() => onCalificar(col.id, col.delivery === "quiz")} />
														</div>
													)
												})}
											</React.Fragment>
										))}
										{l.tasks.length === 0 ? <div className="pcVacio">Aún No Hay Tareas Calificables</div> : null}
									</div>
								) : null}
							</section>
						)
					})}
				</div>
			</>
		)
	}

	// ---- escritorio: la tabla
	return (
		<>
			{barra}
			{resumen}
			<div className="pcTarjeta ntCaja">
				<div className="ntDesliza" tabIndex={0} role="region" aria-label="Libro De Notas">
					<table className="ntTabla">
						<thead>
							<tr>
								<th rowSpan={2} className="ntNombre" scope="col">Alumno</th>
								{grupos.map((g) => g.asistencia ? (
									<th key={g.nombre} rowSpan={2} className="ntGrupo ntColProm" scope="col"><div className="ntGrupoTitulo"><b>{g.nombre}</b></div><div className="pcTenue" style={{ fontWeight: 500 }}>{pesoTxt(g.peso || 0)} · Automática</div></th>
								) : g.cols.length === 0 ? null : (
									<th key={g.nombre} colSpan={g.cols.length + (g.sinCategoria ? 0 : 1)} className="ntGrupo" scope="colgroup">
										<div className={"ntGrupoTitulo" + (g.sinCategoria ? " ntGrupoOro" : "")}>{g.sinCategoria ? <b className="ntGrupoOro">Sin Categoría · No Cuenta</b> : <><b>{g.nombre}</b> · {pesoTxt(g.peso || 0)}</>}</div>
									</th>
								))}
								<th rowSpan={2} className="ntFinal ntGrupo" scope="col" style={{ verticalAlign: "middle" }}>Promedio</th>
							</tr>
							<tr>
								{grupos.map((g) => g.asistencia || g.cols.length === 0 ? null : (
									<React.Fragment key={g.nombre}>
										{g.cols.map((i, k) => {
											const col = l.tasks[i]
											return (
												<th key={col.id} scope="col" className={"ntColTarea" + (k === 0 ? " ntGrupo" : "")}>
													<button type="button" title={col.title + (col.unit ? " · " + col.unit : "")} onClick={() => onCalificar(col.id, col.delivery === "quiz")}>{col.title}</button>
													<div className="ntSub">{puntosTxt(col.points)} Pts{col.fixedPct !== null ? " · " + pesoTxt(col.fixedPct) : ""}</div>
													{col.closed ? <span className="pcChip pcChipOro" style={{ height: 20, fontSize: 11, marginTop: 4 }}>Cerrada</span> : null}
													{col.noWeight ? <span className="pcChip pcChipOro" style={{ height: 20, fontSize: 11, marginTop: 4 }} title="La categoría ya se llenó con pesos fijos">Sin Peso</span> : null}
												</th>
											)
										})}
										{g.sinCategoria ? null : <th scope="col" className="ntColProm" style={{ verticalAlign: "bottom" }}>Prom.</th>}
									</React.Fragment>
								))}
							</tr>
						</thead>
						<tbody>
							{l.students.map((a) => (
								<tr key={a.id}>
									<th scope="row" className="ntNombre"><span className="ntNombreFila"><Avatar a={{ name: a.name, initials: a.initials, photo: a.photo }} size={30} /><span title={a.name}>{a.name}</span></span></th>
									{grupos.map((g) => {
										if (g.asistencia) {
											const c = categoriaDe(a, g.nombre)
											return <td key={g.nombre} className="ntGrupo ntColProm" title={textoAsistencia(a.attendance) || "Sin asistencia tomada"}><span className={esBaja(c?.pct, minimo) ? "ntOro" : undefined}>{!c || c.pct === null ? "–" : notaTxt(c.pct) + " %"}</span></td>
										}
										if (g.cols.length === 0) return null
										const c = g.sinCategoria ? null : categoriaDe(a, g.nombre)
										return (
											<React.Fragment key={g.nombre}>
												{g.cols.map((i, k) => (
													<td key={l.tasks[i].id} className={k === 0 ? "ntGrupo" : undefined}>
														<Celda col={l.tasks[i]} c={a.cells[i]} alumno={a.name} minimo={minimo} onGuardar={(p) => guardar(l.tasks[i], a, p)} onAbrir={() => onCalificar(l.tasks[i].id, l.tasks[i].delivery === "quiz")} />
													</td>
												))}
												{g.sinCategoria ? null : <td className="ntColProm"><span className={esBaja(c?.pct, minimo) ? "ntOro" : undefined}>{!c || c.pct === null ? "–" : notaTxt(c.pct)}</span></td>}
											</React.Fragment>
										)
									})}
									<td className="ntFinal ntGrupo"><Promedio n={a.final} minimo={minimo} /></td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
			<div className="ntLeyenda">
				<span><span className="ntMuestra" style={{ border: "1px dashed var(--pc-acento-borde)" }} />Calificada, Sin Publicar</span>
				<span><span className="ntMuestra" style={{ background: "var(--pc-oro-tinte)" }} />Falta (Cuenta 0 Al Devolver O Cerrar)</span>
				<span><b style={{ color: "var(--pc-oro-texto)" }}>Oro</b> Bajo {notaTxt(minimo)}</span>
				<span>Una categoría sin notas aún no cuenta: su peso se reparte entre las demás.</span>
			</div>
			{l.tasks.length === 0 ? <div className="pcVacio">Aún No Hay Tareas Calificables: el promedio sale por ahora de la asistencia.</div> : null}
		</>
	)
}

export default LibroDeNotasPanel
